import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { clsx } from "clsx";
import { CaretDownFilled } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useAppSelector } from "../../store";
import { navigation, isDropdownItem } from "../../constants";
import type { NavItemType, SidebarProps } from "../../types";

export function Sidebar({ onClose, isMobile = false }: SidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const { sidebarCollapsed } = useAppSelector((state) => state.settings);
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([
    "User Management",
  ]);

  const isCollapsed = !isMobile && sidebarCollapsed;

  const toggleDropdown = (name: string) => {
    setOpenDropdowns((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const isItemActive = (item: NavItemType): boolean => {
    if (isDropdownItem(item)) {
      return item.children.some(
        (child) =>
          pathname === child.href || pathname.startsWith(child.href + "/"),
      );
    }
    return pathname === item.href || pathname.startsWith(item.href + "/");
  };

  const handleLinkClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <aside
      className={clsx(
        "flex flex-col bg-sidebar h-full transition-all duration-300",
      )}
    >
      <div className={clsx("flex flex-col items-center justify-center py-12")}>
        <Link to="/dashboard" onClick={handleLinkClick}>
          <img
            src={isCollapsed ? "/mini_logo.svg" : "/logo.svg"}
            alt="sericex logo"
            className="m-auto w-3/4"
          />
        </Link>
        {!isCollapsed && (
          <p className="text-xs font-semibold text-gray-600 mt-2 tracking-wide">
            Management
          </p>
        )}
      </div>

      {/* Navigation */}
      <nav className="pt-4">
        <ul className={clsx(isCollapsed ? "space-y-1" : "space-y-2")}>
          {navigation.map((item) => {
            const active = isItemActive(item);

            if (isDropdownItem(item)) {
              const isOpen = openDropdowns.includes(item.name);

              // Collapsed sidebar - show icons only with tooltip
              if (isCollapsed) {
                return (
                  <li key={item.name}>
                    <div className="space-y-1 px-1 border-b border-gray-200">
                      {item.children.map((child) => {
                        const isChildActive =
                          pathname === child.href ||
                          pathname.startsWith(child.href + "/");
                        return (
                          <Tooltip
                            key={child.href}
                            title={child.name}
                            placement="right"
                          >
                            <Link
                              to={child.href}
                              onClick={handleLinkClick}
                              className={clsx(
                                "duration-200 p-2 flex items-center justify-center rounded-md ",
                                isChildActive
                                  ? "bg-secondary text-white"
                                  : "text-grey hover:text-secondary",
                              )}
                            >
                              {child.icon}
                            </Link>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </li>
                );
              }

              // Expanded sidebar - show dropdown
              return (
                <li key={item.name}>
                  {/* Dropdown Header */}
                  <button
                    onClick={() => toggleDropdown(item.name)}
                    className={clsx(
                      "flex items-center justify-between w-full px-6 py-2 cursor-pointer transition-colors duration-200",
                      active
                        ? "text-customBlack font-bold"
                        : "text-grey hover:text-secondary",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{item.name}</span>
                    </div>
                    <CaretDownFilled
                      className={clsx(
                        "text-xs text-grey transition-transform duration-200",
                        isOpen ? "-rotate-180" : "rotate-0",
                      )}
                    />
                  </button>

                  {/* Dropdown Items - CSS height animation for smooth transitions */}
                  <div
                    className={clsx(
                      "overflow-hidden transition-all duration-200 border-b border-black/5",
                      isOpen ? "pb-2" : "h-0",
                    )}
                  >
                    <ul className="space-y-1">
                      {item.children.map((child) => {
                        const isChildActive =
                          pathname === child.href ||
                          pathname.startsWith(child.href + "/");
                        return (
                          <li key={child.href}>
                            <Link
                              to={child.href}
                              onClick={handleLinkClick}
                              className={clsx(
                                "duration-200 px-6 py-2 capitalize flex items-center gap-2 border-l-4 transition-colors",
                                isChildActive
                                  ? "text-secondary border-secondary"
                                  : "border-transparent text-grey hover:text-secondary",
                              )}
                            >
                              {child.icon}
                              <span className="text-sm">{child.name}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </li>
              );
            }

            // Regular nav item (non-dropdown)
            if (isCollapsed) {
              return (
                <li key={item.href} className="border-b  pb-2 mb-2 mx-1">
                  <Tooltip title={item.name} placement="right">
                    <Link
                      to={item.href}
                      onClick={handleLinkClick}
                      className={clsx(
                        "duration-200 p-2 flex items-center justify-center rounded-md",
                        active
                          ? "bg-secondary text-white"
                          : "text-grey hover:text-secondary",
                      )}
                    >
                      {item.icon}
                    </Link>
                  </Tooltip>
                </li>
              );
            }

            return (
              <li key={item.href} className="border-b pb-2 mb-2">
                <Link
                  to={item.href}
                  onClick={handleLinkClick}
                  className={clsx(
                    "duration-200 px-6 py-2 capitalize flex items-center gap-2 border-l-4 transition-colors",
                    active
                      ? "text-secondary border-secondary"
                      : "border-transparent text-grey hover:text-secondary",
                  )}
                >
                  {item.icon}
                  <span className="text-sm">{item.name}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}

export default Sidebar;
